import { z } from 'zod';
import {
  VALID_IAP_MODES,
  IAP_DASHBOARD_MODES,
} from 'components/IAP/ICSConstants';

const iapParamsStringUrlSchema = z.object({
  mode: z.enum(VALID_IAP_MODES as [string, ...string[]]),
  phase: z.string().regex(/^\d+$/).optional(),
});
export const iapParamsSchema = iapParamsStringUrlSchema.transform((data) => ({
  mode: data.mode,
  phase: data.phase ? Math.max(0, parseInt(data.phase)) : undefined,
}));
export type IapParams = z.infer<typeof iapParamsSchema>;

const { CURRENT } = IAP_DASHBOARD_MODES;

export const DEFAULT_IAP_PARAMS: IapParams = {
  mode: CURRENT,
  phase: 0,
};

export function validateIapParams(
  searchParams: URLSearchParams
): IapParams | undefined {
  const mode = searchParams.get('mode');
  const phase = searchParams.get('phase');

  if (mode || phase) {
    const params = {
      mode: mode ?? CURRENT,
      phase: phase ?? 0,
    };

    const parsedParams = iapParamsSchema.safeParse(params);
    // TODO TN if they aren't successfully parsed, would we want to log that in Sentry?
    if (parsedParams.success) {
      return parsedParams.data;
    }
  }
}

export function validateIapParamsFromUrl(url: string): IapParams | undefined {
  try {
    const urlObj = new URL(url, window.location.origin);
    return validateIapParams(urlObj.searchParams);
  } catch {
    return undefined;
  }
}

export function validateAndCreateIapSearchParams(
  params: Partial<IapParams>,
  existingParams?: URLSearchParams
): URLSearchParams {
  const validationResult = iapParamsSchema.safeParse({
    mode: params.mode,
    phase: params.phase?.toString(),
  });

  if (!validationResult.success) {
    return new URLSearchParams(existingParams || '');
  }

  const newParams = new URLSearchParams(existingParams || '');

  if (params.mode !== undefined) {
    newParams.set('mode', params.mode);
  }
  if (params.phase !== undefined) {
    newParams.set('phase', params.phase.toString());
  }

  return newParams;
}

export function validateIapRouteStructure(
  pathname: string,
  searchParams: Record<string, string>
) {
  const routeSchema = z.object({
    pathname: z
      .string()
      .regex(
        /^\/iap\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/dashboard$/
      ),
    searchParams: z.union([z.object({}), iapParamsStringUrlSchema]),
  });

  return routeSchema.safeParse({ pathname, searchParams });
}
